import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  IconButton,
} from '@mui/material';
import { Send, Notifications, Image as ImageIcon, Close as CloseIcon } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const NotificationsPage = () => {
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required').max(50, 'Max 50 characters'),
    message: Yup.string().required('Message is required').max(200, 'Max 200 characters'),
    userType: Yup.string().required('User type is required'),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSuccess('');
      setError('');

      // Create FormData to handle image upload
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('message', values.message);
      formData.append('userType', values.userType);
      if (values.specificUserId) {
        formData.append('specificUserId', values.specificUserId);
      }
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await apiService.sendNotificationWithImage(formData);

      if (response.success) {
        setSuccess('Notification sent successfully!');
        resetForm();
        handleRemoveImage();
      } else {
        setError(response.message || 'Failed to send notification');
      }
    } catch (err) {
      setError('Failed to send notification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Send Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Send push notifications to users
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Notifications color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Compose Notification
                </Typography>
              </Box>

              <Formik
                initialValues={{
                  title: '',
                  message: '',
                  userType: 'all',
                  specificUserId: '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Send To</InputLabel>
                          <Select
                            name="userType"
                            value={values.userType}
                            onChange={handleChange}
                            label="Send To"
                          >
                            <MenuItem value="all">All Users</MenuItem>
                            <MenuItem value="passengers">All Passengers</MenuItem>
                            <MenuItem value="drivers">All Drivers</MenuItem>
                            <MenuItem value="specific">Specific User</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {values.userType === 'specific' && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="specificUserId"
                            label="User ID or Phone Number"
                            value={values.specificUserId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="title"
                          label="Notification Title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.title && Boolean(errors.title)}
                          helperText={touched.title && errors.title}
                          placeholder="Special offer!"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="message"
                          label="Notification Message"
                          value={values.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.message && Boolean(errors.message)}
                          helperText={touched.message && errors.message}
                          multiline
                          rows={4}
                          placeholder="Get 50% off on your next ride..."
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Image (Optional)
                        </Typography>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          id="notification-image"
                        />
                        {imagePreview ? (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              maxWidth: 300,
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={handleRemoveImage}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <label htmlFor="notification-image">
                            <Button
                              component="span"
                              variant="outlined"
                              startIcon={<ImageIcon />}
                              sx={{ textTransform: 'none' }}
                            >
                              Upload Image
                            </Button>
                          </label>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Max size: 5MB. Supported formats: JPG, PNG, GIF
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<Send />}
                          disabled={isSubmitting}
                        >
                          Send Notification
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Preview
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Notification Title
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your notification message will appear here...
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Tips:
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  • Keep title under 50 characters
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  • Keep message under 200 characters
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  • Use clear and actionable language
                </Typography>
                <Typography variant="caption" display="block">
                  • Test with a specific user first
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NotificationsPage;
